package com.example.hukakmp.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.viewinterop.HtmlElementView
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement

private const val DEFAULT_MAP_LAT = -42.0
private const val DEFAULT_MAP_LON = -65.0

private fun hasSelectedPoint(latitude: Double, longitude: Double): Boolean =
    latitude != 0.0 || longitude != 0.0

private fun ensureMarker(
    container: HTMLDivElement,
    map: dynamic,
    latitude: Double,
    longitude: Double,
    onCenterChanged: (Double, Double) -> Unit,
) {
    val leaflet = window.asDynamic().L
    if (leaflet == undefined) return

    var marker = container.asDynamic().__hukaMarker
    if (marker == null || marker == undefined) {
        val markerOptions = js("{}")
        markerOptions.draggable = true

        marker = leaflet
            .marker(arrayOf(latitude, longitude), markerOptions)
            .addTo(map)

        marker.on("dragend", {
            val point = marker.getLatLng()
            val lat = (point.lat as Number).toDouble()
            val lon = (point.lng as Number).toDouble()
            onCenterChanged(lat, lon)
            Unit
        })

        container.asDynamic().__hukaMarker = marker
    } else {
        marker.setLatLng(arrayOf(latitude, longitude))
    }
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun HukaLocationMap(
    latitude: Double,
    longitude: Double,
    onCenterChanged: (latitude: Double, longitude: Double) -> Unit,
    modifier: Modifier,
) {
    var latText by remember(latitude) { mutableStateOf(latitude.toString()) }
    var lonText by remember(longitude) { mutableStateOf(longitude.toString()) }
    var locationMessage by remember { mutableStateOf<String?>(null) }
    var mapContainer by remember { mutableStateOf<HTMLDivElement?>(null) }

    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.45f),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Text(
                "Ubicación del parte",
                style = MaterialTheme.typography.titleMedium,
            )
            Text(
                "Tocá el mapa para marcar el lugar, arrastrá el pin o usá la ubicación del dispositivo.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            HtmlElementView(
                modifier = Modifier.fillMaxWidth().height(320.dp),
                factory = {
                    (document.createElement("div") as HTMLDivElement).apply {
                        mapContainer = this
                        style.width = "100%"
                        style.height = "100%"
                        style.borderRadius = "16px"
                        style.setProperty("overflow", "hidden")
                        style.backgroundColor = "#E8F1F5"

                        runWhenLeafletContainerIsSized(this) {
                            val leaflet = window.asDynamic().L
                            if (leaflet == undefined) {
                                innerText = "No se pudo cargar el mapa. Podés ingresar las coordenadas manualmente."
                                return@runWhenLeafletContainerIsSized
                            }

                            val mapOptions = js("{}")
                            mapOptions.zoomControl = true
                            mapOptions.scrollWheelZoom = false

                            val map = leaflet.map(this, mapOptions)
                            asDynamic().__hukaMap = map

                            val selected = hasSelectedPoint(latitude, longitude)
                            val initialLat = if (selected) latitude else DEFAULT_MAP_LAT
                            val initialLon = if (selected) longitude else DEFAULT_MAP_LON
                            val initialZoom = if (selected) 14 else 5

                            map.setView(arrayOf(initialLat, initialLon), initialZoom)

                            val tileOptions = js("{}")
                            tileOptions.maxZoom = 19
                            tileOptions.attribution =
                                "&copy; OpenStreetMap contributors"

                            leaflet
                                .tileLayer(
                                    "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
                                    tileOptions,
                                )
                                .addTo(map)

                            if (selected) {
                                ensureMarker(
                                    container = this,
                                    map = map,
                                    latitude = latitude,
                                    longitude = longitude,
                                    onCenterChanged = onCenterChanged,
                                )
                            }

                            map.on("click", { event: dynamic ->
                                val point = event.latlng
                                val lat = (point.lat as Number).toDouble()
                                val lon = (point.lng as Number).toDouble()

                                ensureMarker(
                                    container = this,
                                    map = map,
                                    latitude = lat,
                                    longitude = lon,
                                    onCenterChanged = onCenterChanged,
                                )
                                onCenterChanged(lat, lon)
                                Unit
                            })

                            attachLeafletResizeHandling(
                                container = this,
                                map = map,
                            )
                        }
                    }
                },
                update = { container ->
                    val map = container.asDynamic().__hukaMap
                    if (
                        map != null &&
                        map != undefined &&
                        hasSelectedPoint(latitude, longitude)
                    ) {
                        ensureMarker(
                            container = container,
                            map = map,
                            latitude = latitude,
                            longitude = longitude,
                            onCenterChanged = onCenterChanged,
                        )
                        map.panTo(arrayOf(latitude, longitude))
                    }
                },
                onRelease = { container ->
                    detachLeafletResizeHandling(container)
                    val map = container.asDynamic().__hukaMap
                    if (map != null && map != undefined) {
                        runCatching { map.remove() }
                    }
                    container.asDynamic().__hukaMap = null
                    container.asDynamic().__hukaMarker = null
                    if (mapContainer === container) {
                        mapContainer = null
                    }
                },
            )

            Text(
                if (hasSelectedPoint(latitude, longitude)) {
                    "Punto seleccionado: ${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}"
                } else {
                    "Todavía no seleccionaste un punto."
                },
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.primary,
            )

            OutlinedButton(
                onClick = {
                    val geo = window.navigator.asDynamic().geolocation
                    if (geo == undefined) {
                        locationMessage = "Este navegador no ofrece geolocalización."
                    } else {
                        locationMessage = "Obteniendo ubicación…"
                        geo.getCurrentPosition(
                            { position: dynamic ->
                                val lat = (position.coords.latitude as Number).toDouble()
                                val lon = (position.coords.longitude as Number).toDouble()
                                latText = lat.toString()
                                lonText = lon.toString()
                                val container = mapContainer
                                if (container != null) {
                                    val map = container.asDynamic().__hukaMap
                                    if (map != null && map != undefined) {
                                        ensureMarker(
                                            container = container,
                                            map = map,
                                            latitude = lat,
                                            longitude = lon,
                                            onCenterChanged = onCenterChanged,
                                        )
                                        map.setView(arrayOf(lat, lon), 16)
                                    }
                                }
                                locationMessage = "Mapa centrado en tu ubicación. Podés ajustar el pin si hace falta."
                                onCenterChanged(lat, lon)
                            },
                            { error: dynamic ->
                                locationMessage = error.message?.toString()
                                    ?: "No se pudo obtener la ubicación."
                            },
                        )
                    }
                },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text("🎯 Centrar en mi ubicación")
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                OutlinedTextField(
                    value = latText,
                    onValueChange = { latText = it },
                    label = { Text("Latitud") },
                    singleLine = true,
                    modifier = Modifier.weight(1f),
                )
                OutlinedTextField(
                    value = lonText,
                    onValueChange = { lonText = it },
                    label = { Text("Longitud") },
                    singleLine = true,
                    modifier = Modifier.weight(1f),
                )
            }

            Button(
                onClick = {
                    val lat = latText.trim().replace(',', '.').toDoubleOrNull()
                    val lon = lonText.trim().replace(',', '.').toDoubleOrNull()
                    if (
                        lat != null &&
                        lon != null &&
                        lat in -90.0..90.0 &&
                        lon in -180.0..180.0
                    ) {
                        locationMessage = "Coordenadas aplicadas al mapa."
                        onCenterChanged(lat, lon)
                    } else {
                        locationMessage = "Revisá las coordenadas ingresadas."
                    }
                },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text("Aplicar coordenadas")
            }

            OutlinedButton(
                onClick = {
                    val selectedLat =
                        if (hasSelectedPoint(latitude, longitude)) latitude else DEFAULT_MAP_LAT
                    val selectedLon =
                        if (hasSelectedPoint(latitude, longitude)) longitude else DEFAULT_MAP_LON
                    window.open(
                        "https://www.openstreetmap.org/?mlat=$selectedLat&mlon=$selectedLon#map=15/$selectedLat/$selectedLon",
                        "_blank",
                    )
                },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text("Abrir en OpenStreetMap")
            }

            locationMessage?.let {
                Text(
                    it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.primary,
                )
            }
        }
    }
}

private fun formatCoordinate(value: Double): String =
    ((value * 100000.0).toInt() / 100000.0).toString()
